import * as React from 'react';
import {Box, ClickAwayListener, InputBase, List, ListItemButton, Paper, Typography} from '@mui/material';
import WordInfoView from "../../vocabulary/components/WordInfoView/WordInfoView";
import {WordWithId} from '../../../common/types/words';

export interface NavbarSearchBarViewProps {
    isOpen: boolean;
    searchText: string;
    setSearchText: (text: string) => void;
    selectedWord: WordWithId | null;
    highlightedIndex: number;
    setHighlightedIndex: (index: number) => void;
    inputRef: React.RefObject<HTMLInputElement>;
    anchorRef: React.RefObject<HTMLDivElement>;
    results: WordWithId[];
    lemmaColumnWidth: string;
    posColumnWidth: string;
    showResults: boolean;
    closeAndReset: () => void;
    handleResultClick: (word: WordWithId) => void;
    handleInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    closeWordInfoView: () => void;
}

export default function NavbarSearchBarView({
    isOpen,
    searchText,
    setSearchText,
    selectedWord,
    highlightedIndex,
    setHighlightedIndex,
    inputRef,
    anchorRef,
    results,
    lemmaColumnWidth,
    posColumnWidth,
    showResults,
    closeAndReset,
    handleResultClick,
    handleInputKeyDown,
    closeWordInfoView,
}: NavbarSearchBarViewProps) {

    return (
        <>
            {isOpen && (
                <ClickAwayListener onClickAway={closeAndReset}>
                    <Box ref={anchorRef}
                         sx={{
                             maxWidth: 1400,
                             mx: 'auto',
                             mt: 2,
                             px: {xs: 1, sm: 2, md: 0},
                             pt: 1.5,
                             borderRadius: 2,
                         }}
                    >
                        <InputBase
                            inputRef={inputRef}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={handleInputKeyDown}
                            placeholder="Search words..."
                            fullWidth={true}
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 2,
                                backgroundColor: theme => theme.palette.primary.main,
                            }}
                        />
                        {showResults && (
                            <Paper sx={{mt: 0.5, maxHeight: 400, overflowY: 'auto', borderRadius: 2}}>
                                <List disablePadding>
                                    {results.map((word, index) => (
                                        <ListItemButton
                                            key={word.wordId}
                                            selected={index === highlightedIndex}
                                            onMouseEnter={() => setHighlightedIndex(index)}
                                            onClick={() => handleResultClick(word)}
                                            sx={{
                                                backgroundColor: theme => theme.palette.secondary.main,
                                                '&.Mui-selected': {
                                                    backgroundColor: theme => theme.palette.secondary.light,
                                                },
                                                '&.Mui-selected:hover': {
                                                    backgroundColor: theme => theme.palette.secondary.light,
                                                },
                                                '&:hover': {
                                                    backgroundColor: theme => theme.palette.secondary.light,
                                                },
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" width="100%" gap={1}>
                                                <Typography sx={{minWidth: lemmaColumnWidth, fontWeight: 'bold'}} noWrap>
                                                    {word.lemma}
                                                </Typography>
                                                <Typography>|</Typography>
                                                <Typography sx={{minWidth: posColumnWidth}} noWrap>
                                                    {word.partOfSpeech ?? ''}
                                                </Typography>
                                                <Typography>|</Typography>
                                                <Typography noWrap>
                                                    {word.translations.join(', ')}
                                                </Typography>
                                            </Box>
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Paper>
                        )}
                    </Box>
                </ClickAwayListener>
            )}
            {selectedWord && (
                <WordInfoView
                    word={selectedWord}
                    onDrawerClosed={closeWordInfoView}
                    actionDispatch={{delete: closeWordInfoView}}
                />
            )}
        </>
    );
}
